import React, { useState, useRef } from 'react';
import { observer } from 'mobx-react';
import useStore from '../../hooks/useStore';
import {
  MessageContainer,
  ChatWindowAvatar,
} from "../../pages/Chatbox/styles/ChatWindowComponent.styled";
import AnnAvatar from '../../images/AnnAvatar.svg';
import UserAvatar from '../../images/UserAvatar.svg';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import DOMPurify from 'dompurify';
import { MarkdownStyles } from '../../elements/ChatBox/MarkdownStyles';
import DescriptionIcon from '@mui/icons-material/Description';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import LinkIcon from '@mui/icons-material/Link';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DocumentCanvas from './DocumentCanvas';
import ContactsChatTool from '../chat/ContactsChatTool';
import VehiclesChatTool from '../chat/VehiclesChatTool';
import WizardMultiSelection from '../chat/MultiSelectionOptions/WizardMultiSelection';
import { detectChatTool, getChatToolProps } from '../../utils/chatMessageDetector';
import { detectMultiSelection } from '../../utils/multiSelectionDetector';
import './DocumentCanvas.styles.css';
import './HomeEnhancedMessage.styles.css';
import {
  MessageWrapperWithCopy,
  MessageTimestamp,
  MessageFooter,
  ActionButton,
  RegeneratingOverlay,
  ModelLabel,
  ImageGalleryWrapper,
  ImageGalleryContainer,
  GalleryImage,
  ImageCountLabel,
  CarouselButton,
  SourceBadge,
  SourceBadgeIcon,
  SourceBadgeText,
  SourcesButton,
  SourcesPanelOverlay,
  SourcesPanel,
  SourcesPanelHeader,
  CloseButton,
  SourceItem
} from './HomeEnhancedMessage.styled';

const HomeEnhancedMessage = observer(({ 
  messageItem, 
  messageIndex,
  displayButtons, 
  width, 
  selectedAgent,
  isTyping,
  isSideBySideActive,
  isRegenerating,
  isLastAgentMessage,
  onAddChatMessage, // Callback to add messages to chat
  onSendToRegularAgent, // Callback to send reasoning result to regular agent
  onEnterDocumentEditMode, // NEW: Callback to enter document edit mode (80/20 split)
  onRegenerateMessage, // NEW: Callback to regenerate message
  onMarkWizardSubmitted, // NEW: Callback to mark wizard as submitted
  showAgentName = true // NEW: Control whether to show agent name in timestamp
}) => {
  const { chatDocumentsStore } = useStore();
  const [isDocumentMode, setIsDocumentMode] = useState(false);
  const [documentTitle, setDocumentTitle] = useState('Document');
  const [currentDocumentId, setCurrentDocumentId] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showSourcesPanel, setShowSourcesPanel] = useState(false);
  const messageRef = useRef(null);
  const galleryRef = useRef(null);
  
  // Detect if message should show special tools
  const chatTool = detectChatTool(messageItem.message) || messageItem.chatTool;
  const isContactsTool = chatTool?.type === 'contacts';
  const isVehiclesTool = chatTool?.type === 'vehicles';
  
  // Detect if message contains multi-selection options
  const multiSelection = detectMultiSelection(messageItem.message);
  const isMultiSelectionTool = !!multiSelection;
  
  const isSpecialTool = isContactsTool || isVehiclesTool || isMultiSelectionTool;
  
  // Check if this is a reasoning agent message
  const isReasoningAgentMessage = 
    messageItem.agentType === 'reasoning' || 
    messageItem.metadata?.isReasoningAgent;
  
  const createMarkup = (content) => {
    return { __html: DOMPurify.sanitize(content, {
      ALLOWED_ATTR: ['class', 'id', 'style'],
      ALLOWED_CSS_PROPERTIES: [
        'color',
        'background-color',
        'font-weight',
        'font-style',
        'text-decoration',
        'text-align',
        'font-size'
      ]
    }) };
  };

  const isHTML = (str) => {
    const doc = new DOMParser().parseFromString(str, "text/html");
    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
  };
  
  // Only show the convert button on received messages (agent responses)
  const shouldShowConvertButton = 
    messageItem.type === 'received' && 
    !isDocumentMode &&
    !isSpecialTool && // Don't show on any special tool
    isDocumentLikeContent(messageItem.message) &&
    displayButtons &&
    !isSideBySideActive;

  // Check if the content looks like a document
  function isDocumentLikeContent(text) {
    // Convert to string first (handles OpenAI array/object formats)
    let messageText = text;
    
    if (typeof messageText !== 'string') {
      if (Array.isArray(messageText)) {
        const textItems = messageText.filter(item => item?.type === 'text' && item?.text);
        if (textItems.length > 0) {
          messageText = textItems.map(item => item.text).join('\n\n');
        } else {
          const fallbackItem = messageText.find(item => item?.text || item?.content || item?.message);
          messageText = fallbackItem?.text || fallbackItem?.content || fallbackItem?.message || '';
        }
      } else if (typeof messageText === 'object' && messageText !== null) {
        messageText = messageText.message || messageText.content || messageText.text || '';
      } else {
        messageText = '';
      }
    }
    
    // If message has significant length, headings, or a structured format
    return messageText && messageText.length > 200;
  }
  
  // Extract a title from the message content
  const extractDocumentTitle = () => {
    if (!messageItem.message) return 'Document';
    
    // Try to find a heading
    const headingMatch = messageItem.message.match(/^\s*#{1,3}\s+([^\n]+)/);
    if (headingMatch) {
      return headingMatch[1].trim();
    }
    
    // Try to use the first line if it's not too long
    const firstLineMatch = messageItem.message.match(/^([^\n]{5,50})\n/);
    if (firstLineMatch) {
      return firstLineMatch[1].trim();
    }
    
    return 'Document';
  };
  
  const handleConvertToDocument = () => {
    const title = extractDocumentTitle();
    
    console.log('📝 Converting to document, callback exists:', !!onEnterDocumentEditMode);
    
    // Create document in store
    const docId = chatDocumentsStore.addDocument({
      title,
      content: messageItem.message,
      agentUuid: selectedAgent?.uuid,
      conversationId: null, // Can be added if conversation tracking is available
      module: 'HomePage'
    });
    
    console.log('📝 Document created with ID:', docId);
    
    // Trigger document edit mode (80/20 split layout)
    if (onEnterDocumentEditMode) {
      console.log('📝 Calling onEnterDocumentEditMode');
      onEnterDocumentEditMode(docId);
    } else {
      console.log('📝 Fallback to inline mode');
      // Fallback to inline mode if callback not provided
      setDocumentTitle(title);
      setCurrentDocumentId(docId);
    setIsDocumentMode(true);
    }
  };
  
  const handleCloseDocument = () => {
    // Document is already saved in store via auto-save
    chatDocumentsStore.clearCurrentDocument();
    setIsDocumentMode(false);
    setCurrentDocumentId(null);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(messageItem.message);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleWizardConfirm = (formattedAnswers, answersData) => {
    console.log('🎯 Wizard confirmed at messageIndex:', messageIndex, 'with data:', answersData);
    
    // Mark this message's wizard as submitted with the actual answers data
    if (onMarkWizardSubmitted) {
      console.log('📝 Calling onMarkWizardSubmitted for index:', messageIndex);
      onMarkWizardSubmitted(messageIndex, answersData);
    }
    
    // Then send the answers (after a small delay to ensure state update)
    setTimeout(() => {
      if (onAddChatMessage) {
        console.log('📤 Sending wizard answers (silent mode)');
        // Send with metadata to indicate this is from wizard
        // This prevents the text from appearing as a regular message in chat
        onAddChatMessage(formattedAnswers, { isWizardAnswer: true });
      }
    }, 100);
  };

  const handleUseInChat = () => {
    if (onSendToRegularAgent && messageItem.message) {
      onSendToRegularAgent(messageItem.message);
    }
  };

  const handleRegenerate = () => {
    if (onRegenerateMessage) {
      onRegenerateMessage(messageIndex);
    }
  };

  const formatTimestamp = () => {
    if (messageItem.type === "sent") {
      return `${messageItem && messageItem.time}`;
    } else {
      // For received messages, optionally show agent name with timestamp
      if (showAgentName && selectedAgent?.name) {
        return (
          <span className="agent-timestamp">
            <span className="agent-name">{selectedAgent.name}</span>
            <span className="timestamp-only"> - {messageItem && messageItem.time}</span>
          </span>
        );
      }
      // Just show the time if agent name should be hidden
      return `${messageItem && messageItem.time}`;
    }
  };

  const formatModelName = (model) => {
    if (!model) return null;
    
    // Format model names for display
    const modelDisplayNames = {
      'gpt-5.2': 'GPT-5.2',
      'gpt-5.2-pro-2025-12-11': 'GPT-5.2 Pro',
      'claude-sonnet-5': 'Claude Sonnet 5',
      'claude-sonnet-4-6': 'Claude Sonnet 4.6',
      'reasoning': 'AI Reasoning',
    };
    
    return modelDisplayNames[model] || model;
  };

  // Carousel navigation functions
  const scrollGallery = (direction) => {
    if (!galleryRef.current) return;
    
    const scrollAmount = 300; // Scroll by 300px
    const currentScroll = galleryRef.current.scrollLeft;
    const targetScroll = direction === 'left' 
      ? currentScroll - scrollAmount 
      : currentScroll + scrollAmount;
    
    galleryRef.current.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  };

  /**
   * Convert message to string format (handles OpenAI array format)
   */
  const convertMessageToString = (message) => {
    if (typeof message === 'string') return message;
    
    if (Array.isArray(message)) {
      // Handle OpenAI's new content array format
      const textItems = message.filter(item => item?.type === 'text' && item?.text);
      if (textItems.length > 0) {
        return textItems.map(item => item.text).join('\n\n');
      }
      // Fallback
      const fallbackItem = message.find(item => item?.text || item?.content || item?.message);
      if (fallbackItem) {
        return fallbackItem.text || fallbackItem.content || fallbackItem.message;
      }
    } else if (typeof message === 'object' && message !== null) {
      if (message.message) return message.message;
      if (message.content) return message.content;
      if (message.text) return message.text;
    }
    
    return '';
  };

  /**
   * Extract ONLY image URLs from message content (AimsOsChatMessage-style)
   * For displaying in the image carousel/gallery
   */
  const extractImageUrls = (content) => {
    const stringContent = convertMessageToString(content);
    if (!stringContent) return [];
    
    // Only match direct image URLs and known image hosting patterns
    const imagePattern = /https?:\/\/[^\s<>"\)]+\.(jpg|jpeg|png|gif|webp|svg|bmp)/gi;
    const unsplashSourcePattern = /https?:\/\/source\.unsplash\.com\/[^\s<>"\)]+/gi;
    const unsplashImagesPattern = /https?:\/\/images\.unsplash\.com\/[^\s<>"\)]+/gi;
    
    const allImageUrls = [
      ...(stringContent.match(imagePattern) || []),
      ...(stringContent.match(unsplashSourcePattern) || []),
      ...(stringContent.match(unsplashImagesPattern) || [])
    ];
    
    // Clean up URLs and remove duplicates
    return allImageUrls
      .map(url => url.replace(/[.,;:!?)]+$/, ''))
      .filter((url, index, self) => self.indexOf(url) === index);
  };

  /**
   * Extract ALL URLs from message content (AimsOsChatMessage-style)
   * For sources button and sources panel (includes images and webpages)
   */
  const extractAllUrls = (content) => {
    const stringContent = convertMessageToString(content);
    if (!stringContent) return [];
    
    // Match ALL HTTP/HTTPS URLs (images, webpages, etc.)
    const urlPattern = /https?:\/\/[^\s<>"\)]+/gi;
    const allUrls = stringContent.match(urlPattern) || [];
    
    // Clean up URLs and remove duplicates
    return allUrls
      .map(url => url.replace(/[.,;:!?)]+$/, ''))
      .filter((url, index, self) => self.indexOf(url) === index);
  };

  return (
    <>
      <MarkdownStyles />
      <MessageContainer 
        itemType={messageItem.type} 
        width={isDocumentMode || isSpecialTool ? "95%" : width} 
        className={`${isDocumentMode ? "document-active" : ""} ${isSpecialTool ? "tool-active" : ""} message-container ${isSideBySideActive ? "side-by-side-active" : ""} ${isReasoningAgentMessage ? "reasoning-agent" : ""}`}
        data-message-type={messageItem.type}
        ref={messageRef}
      >
        {messageItem.type === "sent" || messageItem.type === "file" ?
          <ChatWindowAvatar src={UserAvatar} alt="User Avatar" /> :
          <ChatWindowAvatar src={selectedAgent?.photo_url || AnnAvatar} alt="Agent Avatar" />
        }

        {messageItem.type === "file" ? (
          <MessageWrapperWithCopy itemType="sent">
            <MessageTimestamp>
              {messageItem.time}
            </MessageTimestamp>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f0f7ff', borderRadius: '8px', border: '1px solid #d0e7ff' }}>
              {(() => {
                // Determine icon color based on file type
                const fileName = messageItem.files?.[0]?.name || '';
                const fileExt = fileName.split('.').pop()?.toLowerCase();
                
                let iconColor = '#6C757D'; // Default grey
                if (['doc', 'docx'].includes(fileExt)) {
                  iconColor = '#2B579A'; // Blue for Word
                } else if (['pdf'].includes(fileExt)) {
                  iconColor = '#DC3545'; // Red for PDF
                } else if (['txt'].includes(fileExt)) {
                  iconColor = '#6C757D'; // Grey for Text
                } else if (['xls', 'xlsx', 'csv'].includes(fileExt)) {
                  iconColor = '#28A745'; // Green for Excel
                }
                
                return <DescriptionIcon style={{ fontSize: '32px', color: iconColor }} />;
              })()}
              <div>
                <div style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>
                  {messageItem.files?.[0]?.name || 'Document'}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {messageItem.files?.[0]?.type || 'File'} • {messageItem.files?.[0]?.size || 'Uploaded'}
                </div>
              </div>
            </div>
          </MessageWrapperWithCopy>
        ) : isDocumentMode ? (
          <DocumentCanvas
            documentId={currentDocumentId}
            onClose={handleCloseDocument}
            agentUuid={selectedAgent?.uuid}
            vaultUuid={selectedAgent?.vaultUuid}
            enableAICopilot={true}
            onAddChatMessage={onAddChatMessage}
            className="home-document-editor"
            borderRadius="18px 18px 18px 4px"
            headerBgColor="#F5F7FA"
            footerBgColor="#FFFFFF"
          />
        ) : isContactsTool && messageItem.type === 'received' ? (
          <ContactsChatTool 
            {...getChatToolProps(chatTool.type, messageItem.message)}
          />
        ) : isVehiclesTool && messageItem.type === 'received' ? (
          <VehiclesChatTool 
            {...getChatToolProps(chatTool.type, messageItem.message)}
          />
        ) : isMultiSelectionTool && messageItem.type === 'received' ? (
          <>
            {console.log('🎯 Rendering wizard:', { 
              messageIndex, 
              wizardSubmitted: messageItem.wizardSubmitted,
              hasAnswers: !!messageItem.wizardAnswers,
              questions: multiSelection.questions.length 
            })}
            <WizardMultiSelection
              questions={multiSelection.questions}
              onConfirm={handleWizardConfirm}
              isSubmitted={messageItem.wizardSubmitted || false}
              submittedAnswers={messageItem.wizardAnswers || {}}
              submittedCustomAnswers={messageItem.wizardCustomAnswers || {}}
            />
          </>
        ) : (
          <MessageWrapperWithCopy 
            itemType={messageItem.type}
            className={`message-content-wrapper ${isSideBySideActive ? "side-by-side-active" : ""} ${isReasoningAgentMessage ? "reasoning-agent-message" : ""} ${isRegenerating ? "regenerating" : ""}`}
          >
                <MessageTimestamp>
                  {formatTimestamp()}
                </MessageTimestamp>
            
            {isRegenerating ? (
              <RegeneratingOverlay>
                <CircularProgress size={20} />
                <span>Regenerating response...</span>
              </RegeneratingOverlay>
            ) : (
            <div className={`markdown-content ${isTyping ? 'streaming-message' : ''}`}>
              {(() => {
                // Type validation and conversion to string
                let messageContent = messageItem.message;
                
                if (typeof messageContent !== 'string') {
                  // Try to extract string from object or array (don't log error yet)
                  if (Array.isArray(messageContent)) {
                    // Handle OpenAI's new content array format
                    // Look for items with type 'text' and extract their text property
                    const textItems = messageContent.filter(item => item?.type === 'text' && item?.text);
                    if (textItems.length > 0) {
                      messageContent = textItems.map(item => item.text).join('\n\n');
                    } else {
                      // Fallback: try to find any item with text-like content
                      const fallbackItem = messageContent.find(item => 
                        item?.text || item?.content || item?.message
                      );
                      if (fallbackItem) {
                        messageContent = fallbackItem.text || fallbackItem.content || fallbackItem.message;
                      } else {
                        // Fallback: show error
                        console.error('Message is not a string and cannot be converted:', messageContent);
                        return (
                          <div style={{ color: 'red', padding: '10px', border: '1px solid red' }}>
                            Error rendering message: Invalid message format (array)
                            <details style={{ marginTop: '10px', fontSize: '12px' }}>
                              <summary>Debug info</summary>
                              <pre>{JSON.stringify(messageContent, null, 2)}</pre>
                            </details>
                          </div>
                        );
                      }
                    }
                  } else if (typeof messageContent === 'object' && messageContent !== null) {
                    if (messageContent.message) messageContent = messageContent.message;
                    else if (messageContent.content) messageContent = messageContent.content;
                    else if (messageContent.text) messageContent = messageContent.text;
                    else {
                      // Fallback: show error
                      console.error('Message is not a string and cannot be converted:', messageContent);
                      return (
                        <div style={{ color: 'red', padding: '10px', border: '1px solid red' }}>
                          Error rendering message: Invalid message format
                          <details style={{ marginTop: '10px', fontSize: '12px' }}>
                            <summary>Debug info</summary>
                            <pre>{JSON.stringify(messageContent, null, 2)}</pre>
                          </details>
                        </div>
                      );
                    }
                  } else {
                    console.error('Message is invalid type:', typeof messageContent);
                    return <div style={{ color: 'red' }}>Unable to display message</div>;
                  }
                }

                // Extract image URLs for carousel (ONLY actual images)
                const imageUrls = extractImageUrls(messageContent);
                
                // Extract ALL URLs for sources
                const allUrls = extractAllUrls(messageContent);
                
                // Track domain counts for badges
                const domainCounts = new Map();
                const domainUsage = new Map();
                
                allUrls.forEach((url) => {
                  try {
                    const urlObj = new URL(url);
                    const domain = urlObj.hostname.replace('www.', '').split('.')[0];
                    domainCounts.set(domain, (domainCounts.get(domain) || 0) + 1);
                  } catch (e) {
                    // Skip invalid URLs
                  }
                });
                
                // Don't modify content - let ReactMarkdown handle it
                const cleanedContent = messageContent;

                return (
                  <>
                    {/* AimsOsChatMessage-style Image Gallery with Carousel */}
                    {imageUrls.length > 0 && (
                      <>
                        <ImageCountLabel>
                          {imageUrls.length} image{imageUrls.length > 1 ? 's' : ''}
                        </ImageCountLabel>
                        <ImageGalleryWrapper>
                          {/* Left navigation button */}
                          {imageUrls.length > 1 && (
                            <CarouselButton 
                              $position="left" 
                              onClick={() => scrollGallery('left')}
                              aria-label="Previous images"
                            >
                              <ChevronLeftIcon />
                            </CarouselButton>
                          )}
                          
                          <ImageGalleryContainer ref={galleryRef}>
                            {imageUrls.map((url, index) => (
                              <GalleryImage
                                key={`img-${url}-${index}`}
                                src={url}
                                alt={`Image ${index + 1}`}
                                loading="lazy"
                                onClick={() => window.open(url, '_blank')}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ))}
                          </ImageGalleryContainer>
                          
                          {/* Right navigation button */}
                          {imageUrls.length > 1 && (
                            <CarouselButton 
                              $position="right" 
                              onClick={() => scrollGallery('right')}
                              aria-label="Next images"
                            >
                              <ChevronRightIcon />
                            </CarouselButton>
                          )}
                        </ImageGalleryWrapper>
                      </>
                    )}

                    {/* Message content */}
                    {isHTML(cleanedContent) ? (
                  <div 
                        dangerouslySetInnerHTML={createMarkup(cleanedContent)}
                    className="html-content"
                  />
                ) : (
                  <ReactMarkdown
                    remarkPlugins={[
                      remarkGfm,
                      remarkParse,
                      [remarkRehype, {
                        singleTilde: true,
                        stringLength: true,
                        tablePipeAlign: true,
                        tableCellPadding: true
                      }]
                    ]}
                    rehypePlugins={[
                      rehypeRaw,
                      rehypeStringify,
                      [rehypeSanitize, {
                        allowElements: [
                          'p', 'br', 'b', 'i', 'em', 'strong', 'a', 'img',
                          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                          'ul', 'ol', 'li', 'blockquote', 'code', 'pre',
                          'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
                          'div', 'span', 'del'
                        ],
                        allowAttributes: {
                          'a': ['href', 'target', 'rel'],
                          'img': ['src', 'alt', 'title'],
                          '*': ['class']
                        }
                      }]
                    ]}
                    components={{
                      table: ({node, ...props}) => (
                        <table className="markdown-table" {...props} />
                      ),
                      tr: ({node, ...props}) => (
                        <tr className="markdown-tr" {...props} />
                      ),
                      th: ({node, ...props}) => (
                        <th className="markdown-th" {...props} />
                      ),
                      td: ({node, ...props}) => (
                        <td className="markdown-td" {...props} />
                      ),
                      a: ({node, children, href, ...props}) => {
                        // Extract domain for badge (AimsOsChatMessage-style)
                        let domain = '';
                        let fullDomain = '';
                        let showAsBadge = false;
                        
                        try {
                          const urlObj = new URL(href);
                          fullDomain = urlObj.hostname.replace('www.', '');
                          domain = fullDomain.split('.')[0];
                          
                          // Check if link text is the URL itself
                          const childText = children ? (Array.isArray(children) ? children[0] : children) : '';
                          const normalizedChild = String(childText).trim().replace(/\/+$/, '');
                          const normalizedHref = String(href).trim().replace(/\/+$/, '');
                          
                          // Show as badge if:
                          // 1. No children (empty link text)
                          // 2. Child text matches href (with or without trailing slash)
                          // 3. Child text is the href itself
                          showAsBadge = !children || 
                                       normalizedChild === normalizedHref || 
                                       childText === href ||
                                       (Array.isArray(children) && children[0] === href);
                        } catch (e) {
                          domain = 'link';
                          fullDomain = 'link';
                        }
                        
                        if (showAsBadge && domain) {
                          // Track domain usage for "+X" suffix
                          const currentCount = domainUsage.get(domain) || 0;
                          const totalCount = domainCounts.get(domain) || 1;
                          domainUsage.set(domain, currentCount + 1);
                          
                          // Show "+X" for first occurrence if multiple links from same domain
                          const badgeText = totalCount > 1 && currentCount === 0 
                            ? `${domain} +${totalCount - 1}` 
                            : domain;
                          
                          // Get favicon URL using Google's favicon service
                          const faviconUrl = `https://www.google.com/s2/favicons?domain=${fullDomain}&sz=16`;
                          
                          return (
                            <SourceBadge
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(href, '_blank');
                              }}
                              title={href}
                            >
                              <SourceBadgeIcon 
                                src={faviconUrl} 
                                alt="" 
                                onError={(e) => {
                                  // Hide icon if it fails to load
                                  e.target.style.display = 'none';
                                }}
                              />
                              <SourceBadgeText>{badgeText}</SourceBadgeText>
                            </SourceBadge>
                          );
                        }
                        
                        // Regular link rendering
                        return (
                          <a 
                            className="gfm-link" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            href={href}
                            {...props}
                          >
                            {children || href}
                          </a>
                        );
                      }
                    }}
                  >
                    {cleanedContent}
                  </ReactMarkdown>
                    )}
                  </>
                );
              })()}
            </div>
            )}
            
            {/* Footer with action buttons (AimsOsChatMessage-style) */}
            {messageItem.type === "received" && !isSideBySideActive && !isSpecialTool && !isRegenerating && (
              <MessageFooter>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {isLastAgentMessage && (
                  <ActionButton 
                    onClick={handleRegenerate}
                    title="Regenerate response"
                    aria-label="Regenerate"
                  >
                    <RefreshIcon />
                  </ActionButton>
                )}
                
                <ActionButton 
                onClick={handleCopy} 
                title={copySuccess ? "Copied!" : "Copy message"}
                  aria-label="Copy message"
              >
                {copySuccess ? <CheckIcon /> : <ContentCopyIcon />}
                </ActionButton>
                
                {shouldShowConvertButton && (
                  <ActionButton 
                    onClick={handleConvertToDocument}
                    title="Edit as Document"
                    aria-label="Edit as Document"
                  >
                    <DescriptionIcon />
                  </ActionButton>
                )}
                
                {isReasoningAgentMessage && onSendToRegularAgent && (
                  <ActionButton 
                    onClick={handleUseInChat} 
                    title="Use this response in chat"
                    aria-label="Use in chat"
                    style={{ color: '#4F46E5' }}
                  >
                    <SendIcon />
                  </ActionButton>
                  )}
                </div>
                
                {/* Model label on the right */}
                {messageItem.model && (
                  <ModelLabel>{formatModelName(messageItem.model)}</ModelLabel>
                )}
              </MessageFooter>
            )}
            
            {/* Sources button - only show for received messages with URLs */}
            {messageItem.type === "received" && !isSpecialTool && !isRegenerating && (() => {
              const allUrls = extractAllUrls(messageItem.message);
              return allUrls.length > 0 && (
                <SourcesButton onClick={() => setShowSourcesPanel(true)}>
                  <LinkIcon style={{ fontSize: '16px' }} />
                  {allUrls.length} source{allUrls.length > 1 ? 's' : ''}
                </SourcesButton>
              );
            })()}
          </MessageWrapperWithCopy>
        )}
      </MessageContainer>
      
      {/* Sources Panel Modal */}
      {showSourcesPanel && (() => {
        const allUrls = extractAllUrls(messageItem.message);
        return (
          <>
            <SourcesPanelOverlay onClick={() => setShowSourcesPanel(false)} />
            <SourcesPanel style={{ backgroundColor: '#ffffff' }}>
              <SourcesPanelHeader>
                <h3>{allUrls.length} source{allUrls.length > 1 ? 's' : ''}</h3>
                <CloseButton onClick={() => setShowSourcesPanel(false)}>
                  ✕
                </CloseButton>
              </SourcesPanelHeader>
              
              {allUrls.map((url, index) => {
                let domain = 'Unknown source';
                let isImage = false;
                
                try {
                  const urlObj = new URL(url);
                  domain = urlObj.hostname.replace('www.', '');
                  // Check if it's an image URL
                  isImage = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(url) || 
                            url.includes('unsplash.com');
                } catch (e) {
                  // If URL parsing fails, use a fallback
                  domain = 'Link source';
                }
                
                return (
                  <SourceItem 
                    key={`source-${url}-${index}`} 
                    onClick={() => window.open(url, '_blank')}
                  >
                    <div className="source-domain">{domain}</div>
                    <div className="source-title">
                      {isImage ? `Image ${index + 1}` : `Source ${index + 1}`}
                    </div>
                    <div className="source-url">{url}</div>
                  </SourceItem>
                );
              })}
            </SourcesPanel>
          </>
        );
      })()}
    </>
  );
});

export default HomeEnhancedMessage;